package com.klantenstop.web.data.models.domain

import com.klantenstop.web.data.models.dto.responses.AddressDto
import com.klantenstop.web.data.models.dto.responses.CompanyDto
import com.klantenstop.web.data.models.dto.responses.CompanyPinDto
import com.klantenstop.web.data.models.dto.responses.CoordinatesDto
import com.klantenstop.web.data.models.dto.responses.StatusDto
import kotlinx.datetime.Clock
import kotlinx.datetime.Instant
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.daysUntil
import kotlinx.datetime.toInstant
import kotlinx.datetime.toJSDate
import org.w3c.dom.url.URL

private const val STALE_THRESHOLD_DAYS = 30

/**
 * Shared surface needed to render a map marker + popup. Implemented by both [Company] (the full
 * domain model used by lists/detail pages) and [CompanyPin] (the slim model used by the
 * map-only "pins" fetch), so `toMapPopupHtml()` can be written once and reused by both.
 */
interface MapPinnable {
    val name: String
    val address: Address?
    val statusText: String
    val statusType: Status.Type
    val publicIdentifier: String
}

data class Company(
    val id: String,
    val slug: String,
    override val name: String,
    val description: String,
    override val address: Address?,
    val verified: Boolean,
    val phoneNumber: String,
    val emailAddress: String,
    val website: URL?,
    val logo: MediaFile?,
    val status: Status,
    val categories: List<Category> = emptyList(),
    val createdAt: Instant,
    val updatedAt: Instant
) : MapPinnable {
    val updateAtHumanReadable = run {
        val localeOptions = dateLocaleOptions {
            day = "numeric"
            month = "numeric"
            year = "numeric"
        }

        return@run status.updatedAt.toJSDate().toLocaleString("be", localeOptions)
    }

    val isStale = status.updatedAt.daysUntil(Clock.System.now(), TimeZone.UTC) > STALE_THRESHOLD_DAYS
    override val publicIdentifier: String get() = slug.ifEmpty { id }
    override val statusText: String get() = status.text
    override val statusType: Status.Type get() = status.status
}

data class CompanyPin(
    val id: String,
    val slug: String,
    override val name: String,
    override val address: Address?,
    override val statusText: String,
    override val statusType: Status.Type
) : MapPinnable {
    override val publicIdentifier: String get() = slug.ifEmpty { id }
}

data class Address(
    val streetName: String,
    val streetNumber: String,
    val city: String,
    val postalCode: String,
    val country: String,
    val coordinates: Coordinates
) {
    val fullAddress get() = "$streetName $streetNumber, $postalCode $city, $country"
}

data class Coordinates(
    val latitude: Double = 0.0,
    val longitude: Double = 0.0
)

data class Status(
    val id: String,
    val text: String,
    val status: Type,
    val createdAt: Instant,
    val updatedAt: Instant
) {
    enum class Type {
        Accept,
        Pause,
        Decline,
        Unknown;

        fun toPinIconUrl() = when (this) {
            Accept -> "/status_accept.svg"
            Pause -> "/status_pause.svg"
            Decline -> "/status_decline.svg"
            Unknown -> "/status_unknown.svg"
        }

        fun toMaterialIcon() = when (this) {
            Accept -> "check_circle"
            Pause -> "pause_circle"
            Decline -> "cancel"
            Unknown -> "help"
        }

        fun toText() = when (this) {
            Accept -> "Beschikbaar"
            Pause -> "Gepauzeerd"
            Decline -> "Klantenstop"
            Unknown -> "Onbekend"
        }

        companion object {
            fun fromText(text: String): Type = when (text) {
                "Beschikbaar" -> Accept
                "Gepauzeerd" -> Pause
                "Klantenstop" -> Decline
                "Onbekend" -> Unknown
                else -> Unknown
            }
        }
    }
}

fun AddressDto.toAddress() = Address(
    streetName = streetName,
    streetNumber = streetNumber,
    city = city,
    postalCode = zipCode,
    country = country,
    coordinates = coordinates.toCoordinates()
)

fun CoordinatesDto.toCoordinates() = Coordinates(
    latitude = latitude,
    longitude = longitude
)

fun StatusDto.toStatus() = Status(
    id = id,
    text = text,
    status = status,
    createdAt = LocalDateTime.parse(createdAt).toInstant(TimeZone.UTC),
    updatedAt = LocalDateTime.parse(updatedAt).toInstant(TimeZone.UTC)
)

fun CompanyDto.toCompany() = Company(
    id = id,
    slug = slug,
    name = name,
    description = description,
    address = address?.toAddress(),
    verified = verified,
    phoneNumber = phoneNumber,
    emailAddress = emailAddress,
    website = website.ifEmpty { null }?.let(::normalizeWebsiteUrl),
    logo = logo?.toMediaFile(),
    status = status.toStatus(),
    categories = categories.map { it.toCategory() },
    createdAt = LocalDateTime.parse(createdAt).toInstant(TimeZone.UTC),
    updatedAt = LocalDateTime.parse(updatedAt).toInstant(TimeZone.UTC)
)

// Returns null when the pin has no address — the backend's pins endpoint always inner-joins
// addresses, so this is purely a defensive fallback rather than an expected case.
fun CompanyPinDto.toCompanyPin(): CompanyPin? {
    val companyAddress = address?.toAddress() ?: return null

    return CompanyPin(
        id = id,
        slug = slug,
        name = name,
        address = companyAddress,
        statusText = status.text,
        statusType = status.status
    )
}

fun normalizeWebsiteUrl(website: String): URL? {
    if (website.isBlank()) return null

    val trimmed = website.trim()

    // Check for invalid schemes
    if (trimmed.contains("://")) {
        return when {
            trimmed.startsWith("http://") || trimmed.startsWith("https://") -> {
                try {
                    URL(trimmed)
                } catch (_: Exception) {
                    null
                }
            }
            // Reject any other scheme (javascript://, data://, mailto://, etc.)
            else -> null
        }
    }

    // No scheme provided - validate basic URL structure before prepending https://
    // Check that it contains at least a dot (domain.extension) and doesn't contain invalid characters
    if (!trimmed.contains(".") || trimmed.contains(" ") || trimmed.contains("\n") || trimmed.contains("\t")) {
        return null
    }

    // Prepend https:// and validate
    return try {
        URL("https://$trimmed")
    } catch (_: Exception) {
        null
    }
}
